import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { getAuthUser } from '../../services/authService';
import { useEntity } from '../../context/EntityContext';
import { TableSkeleton, EmptyState, LoadingButton, useToast } from '../../components/ui';

interface Job {
  id: string;
  title: string;
  location: string;
  isRemote: boolean;
  status: string;
  createdAt: string;
  createdById: string;
  createdBy: {
    name: string;
    email: string;
  };
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency: string;
  applicationsCount: number;
}

export const JobManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { showSuccess, showError: showToastError } = useToast();
  const { entity } = useEntity();

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [actionDialog, setActionDialog] = useState<{ open: boolean; action: 'approve' | 'reject' | null }>({
    open: false,
    action: null
  });
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadCurrentUser();
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, entity?.id]);

  const loadCurrentUser = async () => {
    const user = await getAuthUser();
    setCurrentUser(user);
  };

  const loadJobs = async () => {
    try {
      setLoading(true);
      const user = await getAuthUser();

      if (!user) {
        setError('You must be logged in');
        return;
      }

      let query = supabase
        .from('jobs')
        .select(`
          *,
          created_by_user:users!created_by(name, email)
        `)
        .eq('entity_id', user.entityId)
        .order('created_at', { ascending: false });

      // Filter by tab
      if (selectedTab === 0) {
        // Pending approval (pending and draft status)
        query = query.in('status', ['pending', 'draft']);
      } else if (selectedTab === 1) {
        // Active jobs
        query = query.eq('status', 'active');
      } else if (selectedTab === 2) {
        // All jobs
        // No filter
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Filter draft jobs: only the recruiter who created them can see drafts
      // Admin/HR/Manager can only see pending and active jobs, not drafts
      const filteredData = (data || []).filter((job: any) => {
        if (job.status === 'draft') {
          // Only the creator can see their own drafts
          return job.created_by === user.id;
        }
        return true; // All other statuses visible to everyone
      });

      const formattedJobs = filteredData.map((job: any) => ({
        id: job.id,
        title: job.title,
        location: job.location,
        isRemote: job.is_remote,
        status: job.status,
        createdAt: job.created_at,
        createdById: job.created_by,
        createdBy: {
          name: job.created_by_user?.name || 'Unknown',
          email: job.created_by_user?.email || 'N/A'
        },
        salaryMin: job.salary_min,
        salaryMax: job.salary_max,
        salaryCurrency: job.salary_currency,
        applicationsCount: job.applications_count || 0
      }));

      setJobs(formattedJobs);
      setError('');
    } catch (err: any) {
      console.error('Error loading jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveJob = async (jobId: string) => {
    try {
      if (!currentUser) return;
      setActionLoading(true);

      const { error: updateError } = await supabase
        .from('jobs')
        .update({
          status: 'active',
          is_public: true, // Auto-set to public when approved
          approved_by: currentUser.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (updateError) throw updateError;

      showSuccess('Job approved and published successfully!', 'Job Approved');
      setActionDialog({ open: false, action: null });
      setSelectedJob(null);
      loadJobs();
    } catch (err: any) {
      console.error('Error approving job:', err);
      showToastError(err.message || 'Failed to approve job');
      setError(err.message || 'Failed to approve job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectJob = async (jobId: string) => {
    try {
      setActionLoading(true);

      const { error: updateError } = await supabase
        .from('jobs')
        .update({ status: 'closed' })
        .eq('id', jobId);

      if (updateError) throw updateError;

      showSuccess('Job has been rejected and closed.', 'Job Rejected');
      setActionDialog({ open: false, action: null });
      setSelectedJob(null);
      loadJobs();
    } catch (err: any) {
      console.error('Error rejecting job:', err);
      showToastError(err.message || 'Failed to reject job');
      setError(err.message || 'Failed to reject job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!window.confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('jobs')
        .delete()
        .eq('id', jobId);

      if (deleteError) throw deleteError;

      loadJobs();
    } catch (err: any) {
      console.error('Error deleting job:', err);
      setError(err.message || 'Failed to delete job');
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { label: string; color: any }> = {
      draft: { label: 'Draft', color: 'default' },
      pending: { label: 'Pending', color: 'warning' },
      active: { label: 'Approved', color: 'success' },
      closed: { label: 'Closed', color: 'error' }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatSalary = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return 'Not specified';
    const curr = currency || 'USD';
    if (min && max) {
      return `${curr} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    return min ? `${curr} ${min.toLocaleString()}+` : `Up to ${curr} ${max?.toLocaleString()}`;
  };

  if (!currentUser) {
    return <CircularProgress />;
  }

  const canApprove = ['hr', 'admin', 'manager'].includes(currentUser.role || '');

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            bgcolor: '#ffffff',
            border: '1px solid #e0e0e0',
            color: 'text.primary',
            borderRadius: 2
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={2}>
              <WorkIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  Job Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                  Manage and approve job postings
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/admin/jobs/post')}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': { backgroundColor: theme.palette.primary.dark }
              }}
            >
              + Post New Job
            </Button>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Tabs
            value={selectedTab}
            onChange={(_, newValue) => setSelectedTab(newValue)}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '& .MuiTab-root': { fontWeight: 600 }
            }}
          >
            <Tab label={`Pending Approval (${selectedTab === 0 ? jobs.length : '..'})`} />
            <Tab label="Active Jobs" />
            <Tab label="All Jobs" />
          </Tabs>

          {/* Table */}
          <TableContainer>
            {loading ? (
              <Box sx={{ p: 2 }}>
                <TableSkeleton rows={5} columns={7} />
              </Box>
            ) : jobs.length === 0 ? (
              <EmptyState
                variant="jobs"
                title={selectedTab === 0 ? 'No pending jobs' : 'No jobs found'}
                description={
                  selectedTab === 0
                    ? 'There are no jobs waiting for approval. New job postings will appear here.'
                    : 'Start attracting talent by creating your first job posting.'
                }
                actionLabel="Post New Job"
                onAction={() => navigate('/admin/jobs/post')}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Job Title</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Salary</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Applications</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow key={job.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{job.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{job.location}</Typography>
                          {job.isRemote && <Chip label="Remote" size="small" color="primary" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(job.status)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{job.createdBy.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.createdBy.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={job.applicationsCount} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {/* View/Edit - only for non-approved jobs */}
                          {job.status !== 'active' && (
                            <Tooltip title="View/Edit Job">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/admin/jobs/edit/${job.id}`)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* View only for approved jobs */}
                          {job.status === 'active' && (
                            <Tooltip title="View Job (Read Only)">
                              <IconButton
                                size="small"
                                color="default"
                                onClick={() => navigate(`/admin/jobs/edit/${job.id}`)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Approve button - only for pending jobs */}
                          {job.status === 'pending' && canApprove && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<ApproveIcon />}
                              onClick={() => {
                                setSelectedJob(job);
                                setActionDialog({ open: true, action: 'approve' });
                              }}
                              sx={{ mr: 1 }}
                            >
                              Approve
                            </Button>
                          )}

                          {/* Reject button - only for draft/pending */}
                          {(job.status === 'draft' || job.status === 'pending') && canApprove && (
                            <Tooltip title="Reject Job">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSelectedJob(job);
                                  setActionDialog({ open: true, action: 'reject' });
                                }}
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Delete button - available for all jobs if user can approve */}
                          {canApprove && (
                            <Tooltip title="Delete Job">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteJob(job.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        </Paper>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' ? 'Approve Job' : 'Reject Job'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {actionDialog.action === 'approve'
              ? `Are you sure you want to approve the job "${selectedJob?.title}"? It will be visible to the public.`
              : `Are you sure you want to reject the job "${selectedJob?.title}"? It will be marked as closed.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: null })} disabled={actionLoading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color={actionDialog.action === 'approve' ? 'success' : 'error'}
            loading={actionLoading}
            loadingText={actionDialog.action === 'approve' ? 'Approving...' : 'Rejecting...'}
            onClick={() => {
              if (selectedJob) {
                actionDialog.action === 'approve'
                  ? handleApproveJob(selectedJob.id)
                  : handleRejectJob(selectedJob.id);
              }
            }}
          >
            {actionDialog.action === 'approve' ? 'Approve' : 'Reject'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
